const content = `
# Solver

The Vortx Flux solver is a Julia-based optimization engine that solves LP and MILP problems for battery dispatch, peak shaving, and energy arbitrage. It runs as an HTTP server on Google Cloud Run.

## Technology Stack

| Component | Technology |
|-----------|-----------|
| Language | Julia 1.10 (LTS) |
| Solver | HiGHS (open-source LP/MILP) |
| HTTP Server | HTTP.jl |
| Deployment | Docker on Cloud Run |

## What The Solver Does

The solver receives a scenario (equipment specs, load profiles, tariff rates, topology) and returns an optimal dispatch schedule that minimizes electricity cost. It handles:

- **Battery dispatch** -- When to charge/discharge the BESS given time-of-use rates
- **Peak shaving** -- Limiting grid imports to reduce demand charges
- **Solar self-consumption** -- Prioritizing on-site solar use over grid export
- **EV managed charging** -- Shifting EV load to off-peak periods while meeting energy targets

## PhD Simulation vs Solver Optimization

The PhD simulation and the solver serve different purposes:

| Aspect | PhD Simulation | Solver Optimization |
|--------|---------------|-------------------|
| **Goal** | Model power flow through a topology | Minimize cost/peak for a given topology |
| **Method** | Physics-based forward simulation | Mathematical optimization (LP/MILP) |
| **Output** | Energy flow, losses, efficiency | Dispatch schedule, cost savings |
| **Used By** | PhD section (topology comparison) | Site DNA, EMS, Orchestrator |

## API Endpoints

| Method | Endpoint | Description |
|--------|----------|-------------|
| POST | \`/optimize\` | Run a full optimization scenario |
| GET | \`/health\` | Health check |
| GET | \`/status\` | Solver status and version |

## Topology-Aware Power Flow

In PhD mode, the solver traces power through each component in the topology's single-line diagram:

1. **Source generation** -- PV output at each timestep
2. **Conversion losses** -- Each inverter/converter applies its efficiency curve
3. **Bus power balance** -- Power entering a bus must equal power leaving
4. **Storage dynamics** -- BESS SOC updates with charge/discharge and losses
5. **Grid balance** -- Grid makes up any deficit (import) or absorbs surplus (export)

The result is a minute-by-minute power flow trace showing exactly where each unit of energy goes and how much is lost at each conversion point.

## Local Development

\`\`\`bash
cd apps/vortx-flux-solver
export NREL_DEVELOPER_API_KEY="your-key-here"
JULIA_LOAD_PATH="$(pwd)/VortxFlux/src:@:@stdlib" julia --project=. src/server.jl
\`\`\`

The solver starts on port 8000 by default. Julia 1.10 is required -- newer versions have compatibility issues with some dependencies.

## Performance

Typical solve times:
- **LP (battery only)**: 50-200ms
- **MILP (with integer variables)**: 1-10 seconds
- **PhD simulation (24h)**: 200-500ms
- **PhD simulation (7d)**: 1-3 seconds
`;

export default content;
